import React from "react";
import { useTranslation } from "react-i18next";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";
import type { U2000AgingBucket } from "../../../u2000Core/constants/U2000AgingBuckets";
import U3125ArAgingInlineSpinner from "../../../u3125Facture/components/report/U3125ArAgingInlineSpinner";
import { NS } from "../../U3134";
import {
  u3134FormatTecAgeCurrency,
  u3134FormatTecAgeCustomerLabel,
  u3134TecAgeTotalsForBucket,
  type U3134TecAgeCustomerDetailReport,
  type U3134TecAgeSummaryCustomerRow,
} from "../../U3134Service";
import U3134TecAgeCustomerExpandedTable from "./U3134TecAgeCustomerExpandedTable";

export interface U3134TecAgeHierarchySectionProps {
  customers: U3134TecAgeSummaryCustomerRow[];
  expandedCustomerId: string | null;
  loadingCustomerId: string | null;
  customerDetail: U3134TecAgeCustomerDetailReport | null;
  onToggleCustomer: (customerId: string) => void;
  selectedBucket?: U2000AgingBucket | null;
}

const U3134TecAgeHierarchySection: React.FC<U3134TecAgeHierarchySectionProps> = ({
  customers,
  expandedCustomerId,
  loadingCustomerId,
  customerDetail,
  onToggleCustomer,
  selectedBucket = null,
}) => {
  const { t } = useTranslation();

  if (!customers.length) {
    return (
      <p className="text-sm text-muted-foreground">{t("msgTecAgeNoCustomers", { ns: NS })}</p>
    );
  }

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold">{t("lblTecAgeCustomersSection", { ns: NS })}</h3>
      <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
        {customers.map((row) => {
          const customerId = row.customer.id;
          const expanded = expandedCustomerId === customerId;
          const isLoadingDetail = loadingCustomerId === customerId;
          const customerLabel = u3134FormatTecAgeCustomerLabel(row.customer);
          const detailForCustomer =
            expanded && customerDetail?.customer.id === customerId ? customerDetail : null;
          const projects = detailForCustomer?.projects ?? [];

          return (
            <li key={customerId} className="bg-white">
              <button
                type="button"
                className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-gray-50"
                onClick={() => onToggleCustomer(customerId)}
                aria-expanded={expanded}
              >
                <span className="mt-0.5 shrink-0 text-muted-foreground">
                  {expanded ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium">{customerLabel}</div>
                  {!expanded ? (
                    <div className="mt-1 text-xs text-muted-foreground tabular-nums">
                      {(() => {
                        const totals = selectedBucket
                          ? u3134TecAgeTotalsForBucket(row.buckets, selectedBucket)
                          : row.totals;
                        return (
                          <>
                            {t("colTecAgeLineCount", { ns: NS })}: {totals.lineCount} —{" "}
                            {t("colTecAgeTotalAmount", { ns: NS })}: {u3134FormatTecAgeCurrency(totals.totalAmount)}
                          </>
                        );
                      })()}
                    </div>
                  ) : null}
                  {isLoadingDetail ? (
                    <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{t("msgTecAgeLoadingCustomer", { ns: NS })}</span>
                      <U3125ArAgingInlineSpinner className="h-3 w-3" />
                    </div>
                  ) : null}
                </div>
              </button>
              {expanded && !isLoadingDetail && detailForCustomer ? (
                <div className="border-t border-gray-100 px-3 pb-2 pt-1">
                  <U3134TecAgeCustomerExpandedTable
                    customerDetail={detailForCustomer}
                    customerLabel={customerLabel}
                    selectedBucket={selectedBucket}
                  />
                  {!projects.length ? (
                    <p className="mt-1 text-xs text-muted-foreground">{t("msgTecAgeNoProjects", { ns: NS })}</p>
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default U3134TecAgeHierarchySection;
